<template>
  <div class="booking-confirmation">
    <div class="container-fluid">
      <div class="row no-gutters">
        <div class="col-12 col-lg-4 d-print-none">
          <CiCampgroundInfo
            :campground="campground"
            :confirmation="confirmation"
            :countries="countries"
            :currency="currency"
            :dates="[
              confirmation.booking_data.date_from,
              confirmation.booking_data.date_until,
            ]"
            :guests="confirmation.booking_data.campsites[0].persons"
            :isCompleted="isCompleted"
            :urls="urls"
            :language="language"
            :price="confirmation.pricelist"
          >
            <CiSum
              :campgroundCurrency="campground.currency"
              :confirmation="confirmation"
              :isCompleted="isCompleted"
              :language="language"
              :price="confirmation.pricelist"
              :userCurrency="currency"
              slot="sum"
            >
            </CiSum>
          </CiCampgroundInfo>
        </div>
        <div class="col-12 col-lg-8">
          <div class="booking-confirmation__interaction">
            <CiProgressBar :activeStep="6"></CiProgressBar>

            <div class="booking white-box">
              <div class="row">
                <div class="col-12 col-md-2 d-print-none">
                  <div class="booking__status-icon">
                    <div class="status__circle">
                      <iconCheckmarkWhite class="icon"></iconCheckmarkWhite>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-10 print-full">
                  <div class="row">
                    <div class="col-12 col-md-6 print-full">
                      <div class="booking__status-info">
                        <h3 class="booking__headline">
                          {{ $t('pages.bookingConfirmation.statusInfo.title') }}
                        </h3>
                        <p>
                          {{
                            $t('pages.bookingConfirmation.statusInfo.content')
                          }}
                        </p>
                      </div>
                    </div>
                    <div class="col-12 col-md-6 print-full">
                      <div class="booking__status-number">
                        <h3 class="booking__headline">
                          {{
                            $t('pages.bookingConfirmation.statusNumber.title')
                          }}
                        </h3>
                        <span class="booking-number">
                          <iconCampingCard class="cc-icon" v-if="isCampingCardAcsi()"/> 
                          {{ confirmation.booking_number }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="booking__download d-print-none">
                    <div class="row">
                      <div class="col-12 col-md-6">
                        <div class="booking__status-download-text text-center">
                          <h3 class="booking__headline">
                            {{ $t('pages.bookingConfirmation.print.title') }}
                          </h3>
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <div class="booking__status-download-button">
                          <CiButton
                            class="
                              button--tertiary button--small
                              download-button
                            "
                            @click="printPage"
                          >
                            <span>{{
                              $t('pages.bookingConfirmation.print.button')
                            }}</span>
                            <iconArrowWhite
                              class="icon icon--animated"
                            ></iconArrowWhite>
                          </CiButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <CiPaymentInfoBox :type="confirmation.payment_mode" v-if="confirmation.payment_mode == 'bank_transfer'">
              <h3 class="booking__headline">                
                {{ $t('pages.bookingConfirmation.paymentInfoBox.title') }}
              </h3>
              <p>
                <span v-if="!depositIsTotal" v-html="
                $t('pages.bookingConfirmation.paymentInfoBox.depositDescription', {
                  amount: totalDepositAmount(),
                })"></span>
                <span v-else v-html="
                $t('pages.bookingConfirmation.paymentInfoBox.fullAmountDescription', {
                  amount: totalDepositAmount(),
                })"></span>
                <span class="ml-1" v-html="
                $t('pages.bookingConfirmation.paymentInfoBox.bookingNumberInfo', {
                  bookingNumber: confirmation.booking_number
                })"></span>
              </p>
              <div>
                <p class="mb-0"><b>{{ $t('pages.bookingConfirmation.paymentInfoBox.accountHolder') }}:</b> {{ confirmation.payment_bank_transfer.bankaccount_owner }}</p>
                <p><b>{{ $t('pages.bookingConfirmation.paymentInfoBox.iban') }}:</b> {{ confirmation.payment_bank_transfer.bankaccount_iban }}</p>
              </div>
              <p class="email-link">
                {{ $t('pages.bookingConfirmation.paymentInfoBox.additionalInfo') }}
                <a :href="`mailto:${campground.email}`">  {{campground.email}} </a>
              </p>
            </CiPaymentInfoBox>

            <CiPaymentInfoBox :type="confirmation.payment_mode" v-if="confirmation.payment_mode == 'pay_by_link'">
              <h3 class="booking__headline">                
                {{ $t('pages.bookingConfirmation.paymentLinkInfoBox.title') }}
              </h3>
              <p>
                <span v-if="!depositIsTotal" v-html="
                $t('pages.bookingConfirmation.paymentLinkInfoBox.depositDescription', {
                  amount: totalDepositAmount(),
                })"></span>
                <span v-else v-html="
                $t('pages.bookingConfirmation.paymentLinkInfoBox.fullAmountDescription', {
                  amount: totalDepositAmount(),
                })"></span>
                <span class="ml-1" v-html="
                $t('pages.bookingConfirmation.paymentLinkInfoBox.bookingNumberInfo', {
                  bookingNumber: confirmation.booking_number
                })"></span>
              </p>
              <div class="mb-3">
                <CiButton
                  class="button--primary button--small"
                  :disabled="!confirmation.pay_by_link_url"
                  @click="openPayByLink"
                >
                  <span v-html="
                    $t('pages.bookingConfirmation.paymentLinkInfoBox.buttonText', {
                      amount: totalDepositAmount(),
                      bookingNumber: confirmation.booking_number
                    })"></span>
                  <iconArrowWhite
                    class="icon icon--animated"
                  ></iconArrowWhite>
                </CiButton>
                <div class="d-flex align-items-center gap-2 mt-2">
                  <iconLock class="mr-2" /> <p class="mb-0">{{ $t('pages.bookingConfirmation.paymentLinkInfoBox.securePaymentInfo') }}</p>
                </div>
              </div>    
              <p class="email-link">
                {{ $t('pages.bookingConfirmation.paymentLinkInfoBox.additionalInfo') }}
                <a :href="`mailto:${campground.email}`">  {{campground.email}} </a>
              </p>
            </CiPaymentInfoBox>

            <CiInfoBox :type="'warning'">
              <h3 class="booking__headline">
                {{ $t('pages.bookingConfirmation.infoBox.title') }}
              </h3>
              <h4 class="booking__subline">
                {{ $t('pages.bookingConfirmation.infoBox.subtitle') }}
              </h4>
              <p>
                {{
                  $t('pages.bookingConfirmation.infoBox.arrivalDeparture', {
                    arrival: campground.arrival_from,
                    departure: campground.departure_until,
                  })
                }}
              </p>
              <p>
                {{
                  $t(
                    'pages.bookingConfirmation.infoBox.arrivalDepartureAccommodation',
                    {
                      arrival: campground.accommodation_arrival_from,
                      departure: campground.accommodation_departure_until,
                    }
                  )
                }}
              </p>
              <h4 class="booking__subline" v-if="campground.additional_rules">
                {{
                  $t('pages.bookingConfirmation.infoBox.additionalRulesTitle')
                }}
              </h4>
              <p v-html="campground.additional_rules"></p>
            </CiInfoBox>
            <div class="white-box contact__details print-full" v-if="campground.email">
              <div>                      
                  <h3 class="booking__headline mb-1">
                    {{ $t('pages.bookingConfirmation.contactDetails.title') }}
                  </h3>
                  <p>
                    {{ $t('pages.bookingConfirmation.contactDetails.content') }}
                  </p>
            </div>            

                  <div>
                    <div class="contact__details-content">
                      <div>
                        <iconEmail class="icon--small"></iconEmail>
                      </div>
                      <div>
                        <p class="label"> {{ $t('pages.bookingConfirmation.contactDetails.emailLabel') }} </p>
                        <a :href="`mailto:${campground.email}`">
                          {{campground.email}}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
            <h2 class="booking-confirmation__subheading">
              {{ $t('pages.bookingConfirmation.detailsHeadline') }}
            </h2>

            <div class="booking booking--summary white-box">
              <section class="summary__step summary__step--no-border">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('generic.labels.campground') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper">
                        <span class="value">{{ campground.name }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.street1">
                        <span>{{ campground.street1 }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.street2">
                        <span>{{ campground.street2 }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.city">
                        <span
                          >{{ campground.zip_code }} {{ campground.city }}</span
                        >
                      </div>
                      <div class="wrapper" v-if="campground.country">
                        <span>{{ getCountryByCode(campground.country) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section class="summary__step summary__step--no-border">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step1') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper" v-if="dates[0]">
                        <span class="label"
                          >{{ $t('generic.labels.arrival') }}:
                        </span>
                        <span class="value">{{
                          getFormattedDate(dates[0])
                        }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[1]">
                        <span class="label"
                          >{{ $t('generic.labels.departure') }}:
                        </span>
                        <span class="value">{{
                          getFormattedDate(dates[1])
                        }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[0] && dates[1]">
                        <span class="label text-capitalize"
                          >{{ $t('generic.labels.nights') }}:
                        </span>
                        <span class="value">{{ numNights }}</span>
                      </div>

                      <div class="wrapper margin-top">
                        <div
                          class="wrapper"
                          v-for="(person, index) of getGuestData()"
                          :key="index"
                        >
                          <span class="value"
                            >{{ person.amount }}x {{ person.name }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>              
              <section class="summary__step" v-if="getUserCampsites">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step2') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <CiAccommodationList 
                      :userList="getUserCampsites" 
                      :currency="currency" 
                      :language="language" />
                  </div>
                </div>
              </section>
              <section
                class="summary__step summary__step--border-bottom"
                v-if="getGuestBySeason"
              >
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('pages.bookingSummary.priceDetailsHeadline') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <CiGuestList 
                      :guestList="getGuestBySeason" 
                      :currency="currency" 
                      :language="language"/>
                  </div>
                </div>
              </section>

              <!-- subtotal start -->
              <section
                class="
                  summary__sum
                  d-flex
                  justify-content-between
                  align-items-center
                "
              >
                <div class="sum-label">
                  {{ $t('generic.labels.subtotal') }}
                  <CiTooltip class="tooltip">
                    <template slot="title">{{
                      $t('tooltips.subtotal.title')
                    }}</template>
                    <template slot="content">
                      <p v-html="$t('tooltips.subtotal.content')"></p>
                    </template>
                  </CiTooltip>
                </div>
                <div class="sum-value">
                  {{ price.subtotal | currency(currency.code, language.code) }}
                </div>
              </section>
              <!-- subtotal end -->
              <section class="summary__step" v-if="getUserExtrasBySeasons">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step3') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <CiExtrasList 
                      :extrasList="getUserExtrasBySeasons" 
                      :currency="currency" 
                      :language="language" />                     
                  </div>
                </div>
              </section>
              <section
                class="summary__step"
                v-if="getMandatoryExtrasPriceNotZeroBySeason"
              >
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $tc('generic.labels.mandatory_extra', 0) }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <CiMandatoryExtrasList 
                      :extrasList="getMandatoryExtrasPriceNotZeroBySeason" 
                      :currency="currency" 
                      :language="language" />
                  </div>
                </div>
              </section>
              <section class="summary__step">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step4') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div
                        class="wrapper"
                        v-if="billing.name && billing.lastname"
                      >
                        {{ billing.name }} {{ billing.lastname }}
                      </div>
                      <div class="wrapper" v-if="billing.street">
                        {{ billing.street }}
                      </div>
                      <div class="wrapper" v-if="billing.street_additional">
                        {{ billing.street_additional }}
                      </div>
                      <div class="wrapper" v-if="billing.zip && billing.city">
                        {{ billing.zip }} {{ billing.city }}
                      </div>
                      <div class="wrapper" v-if="billing.country">
                        {{ billing.country.label }}
                      </div>
                      <div class="wrapper margin-top" v-if="billing.phone">
                        {{ billing.phone }}
                      </div>
                      <div class="wrapper" v-if="billing.mail">
                        {{ billing.mail }}
                      </div>
                    </div>
                  </div>

                  <div
                    class="col-sm-4"
                    v-if="Object.keys(travellers).length > 0"
                  >
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.travellers') }}
                    </div>
                  </div>
                  <div
                    class="col-sm-8"
                    v-if="Object.keys(travellers).length > 0"
                  >
                    <div class="step__value step__value--inter">
                      <div
                        class="wrapper margin-top"
                        v-for="(traveller, index) in travellers"
                        :key="index"
                      >
                        <div>
                          {{ traveller.first_name }} {{ traveller.last_name }}
                        </div>
                        <span>{{ getFormattedDate(traveller.birthdate) }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="col-sm-4"
                    v-if="car.license_plate || car.length || car.width"
                  >
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.car') }}
                    </div>
                  </div>
                  <div
                    class="col-sm-8"
                    v-if="car.license_plate || car.length || car.width"
                  >
                    <div class="step__value step__value--inter">
                      <div class="wrapper">
                        <div v-if="car.license_plate">
                          {{ car.license_plate }}
                        </div>
                        <div v-if="car.length">
                          {{ car.length }} {{ $t('generic.labels.length') }}
                        </div>
                        <div v-if="car.width">
                          {{ car.width }} {{ $t('generic.labels.width') }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="billing.message">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.message_to_campground') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="billing.message">
                    <div class="step__value step__value--inter">
                      {{ billing.message }}
                    </div>
                  </div>
                </div>
              </section>

              <section
                v-if="confirmation.cancellation_summary"
                class="summary__step summary__step"
              >
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t(`pages.cancellationTerms.headline`) }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div
                        class="wrapper"
                        v-if="!confirmation.is_free_cancellation"
                      >
                        <ul
                          v-for="(term,
                          index) in confirmation.cancellation_summary"
                          :key="index"
                        >
                          <li>
                            {{ $t('pages.cancellationTerms.condition.part1') }}
                            {{ term.cancel_days }}
                            {{ $t('pages.cancellationTerms.condition.part2') }}
                            {{
                              translateBookingSpanText(term.booking_span) +
                                $t('pages.cancellationTerms.condition.part3')
                            }}
                            {{
                              term.amount +
                                $t('pages.cancellationTerms.percent') +
                                `${
                                  language.code === 'it' &&
                                  !term.minimum_checkbox
                                    ? ` ${$t(
                                        'pages.cancellationTerms.condition.part5'
                                      )}`
                                    : ''
                                }` +
                                `${term.minimum_checkbox ? ',' : '.'}`
                            }}
                            <span v-if="term.minimum_checkbox">
                              {{
                                $t('pages.cancellationTerms.condition.part4')
                              }}
                              {{
                                term.minimum_amount +
                                  ' ' +
                                  term.currency +
                                  $t('pages.cancellationTerms.fullStop')
                              }}
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div class="wrapper" v-else>
                        <ul>
                          <li>
                            {{
                              $t('pages.cancellationTerms.freeCancellationText')
                            }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
                <CiSum
                  class="summary__sum d-none d-lg-flex"
                  :campgroundCurrency="campground.currency"
                  :confirmation="confirmation"
                  :isCompleted="isCompleted"
                  :language="language"
                  :price="confirmation.pricelist"
                  :userCurrency="currency"
                  slot="sum"
                >
                </CiSum>
              <!-- <div
                class="summary__warning"
                v-if="
                  getMandatoryExtrasPriceZero &&
                    getMandatoryExtrasPriceZero.length > 0
                "
              >
                <div class="warning-title">
                  <iconWarning class="icon"></iconWarning>
                  {{ $t('pages.checkoutStepThree.infoBox.copy') }}
                </div>
                <div class="warning-content">
                  <div
                    v-for="mandatory in getMandatoryExtrasPriceZero"
                    :key="mandatory.object_id"
                  >
                    <div class="label" v-html="mandatory.description"></div>
                  </div>
                </div>
              </div> -->
            </div>

            <CiSum
              class="d-print-none"
              :campgroundCurrency="campground.currency"
              :confirmation="confirmation"
              :isCompleted="isCompleted"
              :isMobile="true"
              :language="language"
              :price="confirmation.pricelist"
              :userCurrency="currency"
              slot="sum"
            >
            </CiSum>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import bookingDataMixin from '@/mixins/bookingDataMixin'
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import {
  CiButton,
  CiCampgroundInfo,
  CiInfoBox,
  CiProgressBar,
  CiProgressNavigation,
  CiSum,
  CiTooltip,
  CiExtrasList,
  CiMandatoryExtrasList,
  CiAccommodationList,
  CiGuestList,
  CiPaymentInfoBox
} from '@/components'

import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'
import iconCampingCard from '@/assets/icons/camping-card.svg'
import iconEmail from '@/assets/icons/email-icon.svg'
import iconLock from '@/assets/icons/lock-icon.svg'

export default {
  name: 'BookingConfirmation',
  mixins: [bookingDataMixin, bookingPriceMixin],
  components: {
    CiButton,
    CiCampgroundInfo,
    CiInfoBox,
    CiProgressBar,
    CiProgressNavigation,
    CiSum,
    CiTooltip,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconWarning,
    iconCampingCard,
    CiExtrasList,
    CiMandatoryExtrasList,
    CiAccommodationList,
    CiGuestList,
    iconEmail,
    CiPaymentInfoBox,
    iconLock
  },
  created() {
    this.setLoader(false)
  },

  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
      currencies: 'currencies',
      urls: 'urls',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      car: 'car',
      confirmation: 'confirmation',
      dates: 'dates',
      isCompleted: 'isCompleted',
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      travellers: 'travellers',
    }),  
    
    depositIsTotal() {
      return this.confirmation.pricelist.deposit_value === this.confirmation.pricelist.total
    },

    depositIsPercent() {
      return (
        !this.depositIsTotal &&
        this.confirmation.pricelist.deposit_value !== this.confirmation.pricelist.minimum_fixed_deposit
      )
    },
  },
  methods: {
    ...mapActions('application', {
      setLoader: 'setLoader',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    getGuestData() {
      return this.confirmation.booking_data.campsites[0].persons
    },

    printPage() {
      window.print()
    },

    translateBookingSpanText(bookingSpan) {
      if (bookingSpan === 'before arrival') {
        return this.$t('pages.cancellationTerms.beforeArrival')
      } else {
        return this.$t('pages.cancellationTerms.afterBooking')
      }
    },
    isCampingCardAcsi() {
      return this.confirmation.booking_data.choice === 'campingcard-acsi'
    },
    totalDepositAmount() {     
      const price = this.confirmation.pricelist;

      let amount;

      if (this.depositIsPercent) {
        amount = price.deposit_value;
      } else if (this.depositIsTotal) {
        amount = price.total;
      } else {
        amount = price.deposit_value;
      }
      
      return this.$options.filters.currency(
        amount,
        this.currency.code,
        this.language.code
      )
    },
    openPayByLink() {
      const url = this.confirmation.pay_by_link_url
      if (!url) return
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  },
  mounted() {
    this.trackEcommerceEvents(6)
  },
}
</script>

<style lang="scss" scoped>
.booking-confirmation {
  min-height: 100vh;

  @include media-breakpoint-up(lg) {
    > .container-fluid {
      padding: 0;
    }

    > .container-fluid,
    > .container-fluid > .row {
      min-height: 100vh;
    }
  }

  @media print {
    min-height: 0;
  }
}

.booking-confirmation__interaction {
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 0 30px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 35px 0;
  }

  @media print {
    display: block;
  }

  .booking__headline {
    @include font-format(1rem, 1.25rem);

    margin: 0 0 15px 0;
  }

  .booking__subline {
    margin: 0;
  }
}

.booking-confirmation__subheading {
  @include font-format(1.625rem, 1.875rem);

  margin: 40px 0 20px;
  text-align: center;

  @media print {
    page-break-before: always;
    padding-top: 30px;
    margin: 0 0 20px;
  }
}

.booking {
  border-color: $primary-green;
  margin: 0;
  padding: 20px;
  position: relative;
  text-align: center;

  @include media-breakpoint-up(md) {
    padding: 35px;
    text-align: left;
  }

  &.booking--summary {
    padding-top: 10px;
    margin-bottom: 120px;
    text-align: left;

    @include media-breakpoint-up(lg) {
      margin-bottom: 30px;
    }

    @media print {
      margin: 0;

      .col-sm-4 {
        flex: 0 0 33.33333%;
        max-width: 33.33333%;
      }

      .col-sm-8 {
        flex: 0 0 66.66667%;
        max-width: 66.66667%;
      }
    }
  }

  @media print {
    padding: 20px;
    text-align: center;

    .print-full {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
}

.booking__status-icon {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  margin-bottom: 20px;

  @include media-breakpoint-up(md) {
    margin-bottom: 0;
  }

  .status__circle {
    background: $primary-green;
    border-radius: 50%;
    display: flex;
    height: 60px;
    justify-content: center;
    opacity: 1;
    width: 60px;

    .icon {
      width: 35px;
      height: auto;
    }
  }
}

.booking__status-number {
  .booking-number {
    @include font('gotham_mediumregular');
    @include font-format(2rem, 2rem);

    color: $primary-green;
    display: flex;
    align-items: baseline;
    justify-content: center;
    @include media-breakpoint-up(md) {
      justify-content:flex-start;
    }
    .cc-icon {
      transform: scale(1.2); 
      transform-origin: center; 
      margin-right: 10px;
    }
  }
}

.booking__download {
  margin-top: 20px;
}

.booking__status-download-button,
.booking__status-download-text {
  align-items: center;
  display: flex;
  height: 100%;

  .booking__headline {
    margin: 0;
  }
}
.booking__status-download-text {
  justify-content: center;
}


.download-button {
  margin: 20px auto 0;

  @include media-breakpoint-up(md) {
    margin: 0;
  }
}

.summary__step {
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);

  border-top: 1px solid #979797;
  padding: 25px 0;
  position: relative;

  .step__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.125rem);

    margin: 0 0 20px 0;

    &.step__label--inter {
      @include font('gotham_bookregular');

      margin: 0;
    }

    &.step__label--top {
      margin-top: 20px;
    }
  }

  .step__value {
    &.step__value--inter {
      margin-top: 20px;
    }
    &.step__value--margin-bottom {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .wrapper {
      padding-right: 40px;

      @include media-breakpoint-up(md) {
        padding-right: 50px;
      }

      &.margin-top {
        margin-top: 20px;
      }

      &.no-padding {
        padding: 0;
      }
    }

    .value {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 1.125rem);

      small {
        @include font('gotham_bookregular');
      }
      &.margin-bottom {
        margin-bottom: 20px;
      }
    }


    .strike-through {
      text-decoration: line-through;
      margin-right: 0.4rem;
    }
    .hide-content {
      display: none !important;
    }

    .prices {
      display: block;
    }

    .season__label {
      margin-bottom: 10px;
    }
  }

  .step__edit {
    position: absolute;
    top: 20px;
    right: 0;
    cursor: pointer;
  }

  &.summary__step--border-bottom {
    border-bottom: 1px solid #979797;
  }

  &.summary__step--no-border {
    border: 0;
  }
}

.summary__sum {
  background-color: rgba(155, 155, 155, 0.1);
  min-height: 50px;
  margin: 10px 0;

  .sum-label {
    display: flex;
    align-items: center;
    margin-left: 15px;
    height: 100%;
  }

  .sum-value {
    margin-right: 15px;
  }

  @media print {
    color: black !important;
    display: flex !important;
  }
}
.strike-through {
  text-decoration: line-through;
  margin-right: 0.4rem;
}
.summary__warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  background-color: #fefcdd;
  border: 1px solid #f8e71c;
  margin: 15px 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 10px 25px 10px 60px;
  }  
}

.contact__details {
  border-color: $primary-green;
  margin: 0;
  padding: 20px;

  @include media-breakpoint-up(md) {
    padding: 35px;
  }
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);
  &-content {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    .label {
      font-weight: bold;
      margin-bottom: 0.25rem;
    }
    .icon--small {
      width: 21px;
      height: 18px;
      position: relative;
      top: -3px;
    }
    a {
      color: $primary-green;
    }
  }
  @media print {
    padding: 20px;

    .print-full {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
}

.email-link a {
  color: $primary-green;
}
</style>
